/** Ease-out cubic — no bounce, matches refined SVG timing. */
export function easeOutCubic(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 3);
}

export function easeInOutCubic(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2;
}

/** Quantize progress to limit geometry rebuild frequency (~25 steps/sec). */
export function quantizeProgress(t: number, stepsPerSecond = 25): number {
  const step = 1 / stepsPerSecond;
  return Math.round(t / step) * step;
}

export function segmentProgress(
  elapsed: number,
  start: number,
  duration: number,
): number {
  if (elapsed <= start) return 0;
  if (elapsed >= start + duration) return 1;
  return easeOutCubic((elapsed - start) / duration);
}

/** Smooth ease-in-out segment (settle / idle blends). */
export function segmentProgressSmooth(
  elapsed: number,
  start: number,
  duration: number,
): number {
  if (elapsed <= start) return 0;
  if (elapsed >= start + duration) return 1;
  return easeInOutCubic((elapsed - start) / duration);
}

/** Linear 0→1 (for line draw / fades that should feel mechanical). */
export function segmentLinear(
  elapsed: number,
  start: number,
  duration: number,
): number {
  if (elapsed <= start) return 0;
  if (elapsed >= start + duration) return 1;
  return (elapsed - start) / duration;
}

/**
 * Intro beat sheet:
 * 1) Graph dots + logo S morph begin together
 * 2) Pencil lines start (~after first wave of dots)
 * 3) IN / IGHT / FEATURES follow the S assemble
 */
export const INTRO_DURATION = 5.4;

export const INTRO_TIMELINE = {
  /** Staggered random fade-in of nodes (no explode) — front-loaded so the field reads early. */
  graphDots: { start: 0.0, duration: 0.95 },
  /** Pencil strokes — long independent cascade; keeps drawing behind logo/FEATURES. */
  graphLines: { start: 0.28, duration: 4.2 },
  /** S morph / assemble — starts with the dots. */
  middle: { start: 0.0, duration: 1.45 },
  top: { start: 0.2, duration: 1.45 },
  bottom: { start: 0.4, duration: 1.5 },
  sSpin: { start: 0.0, duration: 1.7 },
  lettersIN: { start: 1.35, duration: 1.0 },
  lettersIGHT: { start: 1.55, duration: 1.1 },
  /** Overlaps IGHT — letters fly in from scattered origins. */
  features: { start: 1.65, duration: 1.85 },
  /** Ramp idle breathe in — ends exactly at INTRO_DURATION (no hold/freeze). */
  settle: { start: 3.5, duration: 1.9 },
} as const;

/**
 * Copy + poke fire once FEATURES is mostly in — section feels like it
 * bumps the lockup from below.
 */
export const COPY_REVEAL_AT = INTRO_TIMELINE.features.start + 0.7;

/** Shared with CSS `hero-copy-bounce-in` — keep in lockstep. */
export const COPY_POP_DURATION = 1.65;

export const FEATURES_LETTERS = "FEATURES";

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Headless audit tools (GTmetrix, Lighthouse, PSI) — skip WebGL entirely.
 * Prefer the boot-script flag (`data-lite-gfx`) so every consumer agrees
 * before React hydrates.
 */
export function isPerformanceAudit(): boolean {
  if (typeof window === "undefined") return false;

  // Boot script / prior decision for this page load.
  try {
    if (document.documentElement.getAttribute("data-lite-gfx") === "1") {
      return true;
    }
  } catch {
    /* ignore */
  }

  const nav = navigator as Navigator & {
    webdriver?: boolean;
    userAgentData?: { brands?: Array<{ brand: string; version: string }> };
  };

  if (nav.webdriver) return true;

  const ua = nav.userAgent || "";
  if (
    /GTmetrix|Chrome-Lighthouse|PTST|HeadlessChrome|PageSpeed|Speed Insights|WebPageTest|Pingdom|DatadogSynthetics|\bLighthouse\b/i.test(
      ua,
    )
  ) {
    return true;
  }

  const brands = nav.userAgentData?.brands;
  if (brands?.some((b) => /Lighthouse|HeadlessChrome/i.test(b.brand))) {
    return true;
  }

  const w = window as Window & {
    __playwright__binding__?: unknown;
    __pwInitScripts?: unknown;
    _phantom?: unknown;
    callPhantom?: unknown;
    __webdriver_evaluate?: unknown;
    __selenium_evaluate?: unknown;
    __driver_evaluate?: unknown;
  };
  if (
    "__playwright__binding__" in w ||
    "__pwInitScripts" in w ||
    "_phantom" in w ||
    "callPhantom" in w ||
    "__webdriver_evaluate" in w ||
    "__selenium_evaluate" in w ||
    "__driver_evaluate" in w
  ) {
    return true;
  }

  try {
    if (new URLSearchParams(window.location.search).has("psi")) return true;
  } catch {
    /* ignore */
  }

  return false;
}

/** Skip all WebGL (hero + tech graph) for audits / reduced motion. */
export function isLiteGraphics(reducedMotion?: boolean | null): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (document.documentElement.getAttribute("data-lite-gfx") === "1") {
      return true;
    }
    if (document.documentElement.getAttribute("data-hero") === "lite") {
      return true;
    }
  } catch {
    /* ignore */
  }
  return isPerformanceAudit() || prefersReducedMotion() || Boolean(reducedMotion);
}

export function markLiteGraphics(): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-lite-gfx", "1");
  document.documentElement.setAttribute("data-hero", "lite");
  document.documentElement.setAttribute("data-hero-copy", "1");
}

export function hasSeenIntro(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem("insight-hero-intro") === "1";
  } catch {
    return false;
  }
}

export function markIntroSeen(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem("insight-hero-intro", "1");
  } catch {
    /* ignore */
  }
}
